import {
  DT,
  applySnapCar,
  stepCarOnTrack,
  stepEliminatedCar,
  v,
  wrapAngle,
  type CarSnap,
  type CarState,
  type CarTuning,
  type InputFrame,
  type PreparedTrack,
  type RacePhase,
  type Snapshot,
  type Vec2,
} from '@nanocars/shared';

// Phases where the server does not step car physics at all (see
// shared/src/sim/step.ts stepRace: only 'racing' calls stepCarOnTrack).
// Prediction must mirror that exactly, or a locally-predicted car would
// drift away from a car the server is holding still.
const FROZEN_PHASES: readonly RacePhase[] = ['countdown', 'roundEnd', 'finished'];

// How long a position/heading correction takes to fully blend into the
// rendered (visual) transform. This is a genuine linear ramp to zero (not a
// multiplicative/exponential decay): the residual visual offset shrinks in
// direct proportion to time-since-correction and is clamped to exactly zero
// once ERROR_DECAY_S has elapsed, so a 1m correction is fully absorbed by
// 200ms and comfortably under any threshold by 250ms.
const ERROR_DECAY_S = 0.2;

// Corrections larger than this teleport instead of blending - a small drift
// correction should never be visible as a slide, but a large one (a
// respawn, a missed collision) should never crawl across the map for 200ms.
const ERROR_SNAP_DIST = 3;

// A snapshot arrives every tick in real play, and once prediction has
// converged the "correction" it produces is pure floating-point replay
// noise (~1e-13), not a real misprediction. Without this floor, every
// onSnapshot call would restart the 200ms decay clock from zero forever
// (there is always SOME correction, however tiny), so an old, already-blended
// error would never actually finish decaying. Anything at or below this
// magnitude is treated as "no correction happened" and leaves the existing
// decay in progress alone.
const CORRECTION_EPSILON = 1e-6;

function freshCar(id: number): CarState {
  return {
    id,
    pos: { x: 0, y: 0 },
    heading: 0,
    vel: { x: 0, y: 0 },
    angVel: 0,
    airborne: 0,
    progress: 0,
    lap: 0,
    lapProgress: 0,
    wrongWayTime: 0,
    backDist: 0,
    eliminated: false,
    surface: 'table',
  };
}

// Client-side prediction for the locally-controlled car: every local input
// tick replays through the SAME stepCarOnTrack the server uses (never a
// local physics copy), and each server snapshot reconciles the predicted
// state by dropping acked inputs and replaying the rest. The raw
// reconciliation can move `car` instantly (a snapshot always sets it
// directly from the server's value); `visual()` is what RaceScene should
// actually render, since it hides that jump behind a short blend.
export class Prediction {
  readonly car: CarState;

  private pending: InputFrame[] = [];
  private phase: RacePhase = 'countdown';

  // The residual visual error (predicted-minus-corrected) at the moment of
  // the last correction, and how long ago that correction happened. The
  // *currently displayed* error is this value scaled down linearly by
  // elapsed/ERROR_DECAY_S - see currentError().
  private errPos0: Vec2 = { x: 0, y: 0 };
  private errHeading0 = 0;
  private errAge = ERROR_DECAY_S;

  constructor(
    private readonly track: PreparedTrack,
    private readonly tuning: CarTuning,
    carId: number,
  ) {
    this.car = freshCar(carId);
  }

  // Called every local sim tick: queues the input as "pending" (unacked)
  // and, unless the race is in a frozen phase, steps the predicted car with
  // it immediately so the local player sees instant response.
  pushInput(f: InputFrame): void {
    this.pending.push(f);
    if (FROZEN_PHASES.includes(this.phase)) {
      this.pending = [];
    } else if (this.car.eliminated) {
      // The server ignores an eliminated car's input entirely and coasts it
      // (stepRace -> stepEliminatedCar). Driving it through stepCarOnTrack
      // here would apply throttle the server never applied, so the local car
      // would visibly keep driving until the next snapshot yanked it back.
      // Pending inputs are dropped for the same reason there is nothing to
      // replay: the server will never ack an input it did not apply.
      this.pending = [];
      stepEliminatedCar(this.car, this.tuning, DT);
    } else {
      stepCarOnTrack(this.track, this.car, f, this.tuning);
    }
    this.errAge = Math.min(ERROR_DECAY_S, this.errAge + DT);
  }

  // Reconciles against an authoritative snapshot: drops every pending input
  // the server has already applied (seq <= ack), resets `car` straight from
  // serverCar (so airborne/eliminated always come from the server, never a
  // local guess), then replays whatever inputs are still unacked. The
  // resulting jump in `car` is absorbed into the decaying visual error
  // rather than shown directly.
  onSnapshot(s: Snapshot, serverCar: CarSnap): void {
    this.phase = s.phase;
    this.pending = this.pending.filter((f) => f.seq > s.ack);

    const oldPos = this.car.pos;
    const oldHeading = this.car.heading;
    const carryPos = this.currentErrPos();
    const carryHeading = this.currentErrHeading();

    applySnapCar(this.car, serverCar, this.track.length);

    if (FROZEN_PHASES.includes(this.phase) || this.car.eliminated) {
      // applySnapCar has just set `eliminated` from the server, so this also
      // catches the tick elimination happens on: replaying inputs the server
      // will never apply would immediately re-introduce the divergence the
      // reconciliation just removed.
      this.pending = [];
    } else {
      for (const f of this.pending) {
        stepCarOnTrack(this.track, this.car, f, this.tuning);
      }
    }

    const deltaPos = v.sub(this.car.pos, oldPos);
    const deltaHeading = wrapAngle(this.car.heading - oldHeading);

    if (v.len(deltaPos) <= CORRECTION_EPSILON && Math.abs(deltaHeading) <= CORRECTION_EPSILON) {
      // Prediction already matched the server; do not disturb whatever
      // decay was already in progress.
      return;
    }

    const newErrPos = v.add(carryPos, deltaPos);
    const newErrHeading = wrapAngle(carryHeading + deltaHeading);

    if (v.len(newErrPos) > ERROR_SNAP_DIST) {
      this.errPos0 = { x: 0, y: 0 };
      this.errHeading0 = 0;
      this.errAge = ERROR_DECAY_S;
    } else {
      this.errPos0 = newErrPos;
      this.errHeading0 = newErrHeading;
      this.errAge = 0;
    }
  }

  // What RaceScene should actually draw for the local car: the raw
  // predicted state minus whatever correction error is still blending out.
  visual(): { pos: Vec2; heading: number } {
    return {
      pos: v.sub(this.car.pos, this.currentErrPos()),
      heading: wrapAngle(this.car.heading - this.currentErrHeading()),
    };
  }

  private currentErrPos(): Vec2 {
    const frac = Math.max(0, 1 - this.errAge / ERROR_DECAY_S);
    return v.scale(this.errPos0, frac);
  }

  private currentErrHeading(): number {
    const frac = Math.max(0, 1 - this.errAge / ERROR_DECAY_S);
    return this.errHeading0 * frac;
  }
}
